import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@app/env';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SnackBarNotificationService } from '../core/notifications/snackbar-notification.service';
import { TrackingService } from './tracking.service';

@Injectable({
  providedIn: 'root'
})
export class ContactUsService {
  private http = inject(HttpClient);
  private dialog = inject(MatDialog);
  private snackbarNotifications = inject(SnackBarNotificationService);
  private trackingService = inject(TrackingService);

  postContacts(contactData: ContactData, token: string): Observable<ContactData> {
    return this.http.post<ContactData>(`${environment.api_url}/Administration/account/ContactUs`, contactData, {
      headers: { 'g-recaptcha-token': token }
    });
  }

  checkBookingConfirmation(guid: string): Observable<HttpResponse<string>> {
    return this.http.get<string>(`${environment.api_url}/Administration/account/booking/${guid}`, {
      observe: 'response'
    });
  }

  public getBookedHours(bookingDate: string): Observable<HoursDisp> {
    return this.http.get<HoursDisp>(`${environment.api_url}/Administration/account/checkdate/${bookingDate}`);
  }

  public getCalendarEvents() {
    const timeMin = new Date();
    const timeMax = new Date();
    timeMax.setMonth(timeMax.getMonth() + 1);
    const params = {
      key: environment.googleCalendar.apiKey,
      timeMin: timeMin.toISOString(),
      timeMax: timeMax.toISOString()
    };
    return this.http
      .get<{
        items: Array<GoogleCalendarEvent>;
      }>(`https://www.googleapis.com/calendar/v3/calendars/${environment.googleCalendar.calendarId}/events`, {
        params: { ...params }
      })
      .pipe(
        map(response => {
          if (response?.items && response?.items.length) {
            return response.items.map(({ start, end }) => ({ start, end }));
          } else {
            return [];
          }
        }),
        catchError(() => of([]))
      );
  }

  public bookADemo(): void {
    this.trackingService.trackEvent('Book_Demo_Click');

    this.getCalendarEvents()
      .pipe(
        switchMap(calendarEvents =>
          from(import('../anonymous/components/book-schedule-time-modal/book-schedule-time-modal.component')).pipe(
            switchMap(({ BookScheduleTimeModalComponent }) =>
              this.dialog
                .open(BookScheduleTimeModalComponent, {
                  panelClass: 'emp-mat-modal',
                  width: '400px',
                  data: { calendarEvents }
                })
                .afterClosed()
            )
          )
        )
      )
      .subscribe({
        next: success => {
          if (success) {
            this.snackbarNotifications.success('Success!');
          }
        }
      });
  }
}

export interface ContactData {
  email: string;
  booking_hour_from: number;
  booking_hour_to: number;
  booking_Date: string;
}

export interface HoursDisp {
  hours: number[];
}

export interface GoogleCalendarEvent {
  start: {
    date?: string;
    dateTime?: string;
  };
  end: {
    date?: string;
    dateTime?: string;
  };
}
